/**
 * Carrier MCP, Billing v3.0 (stdio fork)
 *
 * A byte-for-byte fork of apps/mcp-server/src/billing.ts except for the
 * `!env.CARRIER_USERS` guards, which stdio needs and the Worker does not: a CLI
 * running on a laptop has no Workers KV binding, and without a guard the async
 * IIFEs below crash the Node process with "Cannot read properties of null
 * (reading 'get')" AFTER returning a tool result, breaking every later
 * JSON-RPC call on the same stdin.
 *
 * Every exported POLICY value (tier names, call limits, scopes, the carrier_ask
 * allowances and prices, the portal URLs) is asserted equal to the Worker's by
 * apps/mcp-stdio/test/billing-parity.test.js. That test exists because these
 * two files drifted a full major version apart: the CLI shipped v2.0 tiers and
 * prices to npm while the Worker charged v3.0.
 *
 * Tier model:
 *   free, Unlimited regular OCS/intelligence tool calls (read scope).
 *                carrier_ask: 5 calls/day free, then CC-required pay-per-use at $0.02/call
 *                (Stripe USD minimum $0.50 per card charge; unused balance is kept as prepaid credit).
 *                No subscription required, CC linkable on free plan.
 *   pro, Unlimited regular + intelligence tool calls (read+write scope). $49/mo.
 *                carrier_ask: 1,000 calls/mo included, then $0.01/call overage (auto-refill).
 *   enterprise, Unlimited everything (read+write+admin scope). Custom pricing.
 *
 * carrier_ask cost basis:
 *   Our cost:  ~$0.0058/call (Claude Haiku 4.5 on Bedrock, ~4,776 input + ~200 output tokens)
 *   Pro overage: $0.01/call (1.5× cost, rounded up)
 *   Free PPU:    $0.02/call (2× Pro overage = ~3.4× cost)
 *
 * Storage layout (CARRIER_USERS KV):
 *   key: user:<sub>                    value: UserRecord (tier field drives all gate checks)
 *   key: usage:<sub>:<yyyymm>          value: JSON { calls: number }  (TTL: 35 days)
 *                                      OCS/regular tool calls. Not AI.
 *   key: ai_usage:<sub>:month:<yyyymm> value: JSON { calls: number }  (TTL: 35 days)
 *   key: ai_usage:<sub>:day:<yyyymmdd> value: JSON { calls: number }  (TTL: 2 days)
 *                                      THE AI allowance, owned by @carrier/ai
 *                                      (packages/carrier-ai/src/budget.ts) and shared by
 *                                      carrier_ask, the intelligence composites, the REST
 *                                      API and the console. Pro is monthly, free is daily.
 *   key: stripe_customer_id:<sub>      value: Stripe customer ID string
 *   key: stripe_sub_item_id:<sub>      value: Stripe subscription item ID (Pro metered)
 *   key: stripe_pm_id:<sub>            value: Default Stripe payment method ID (Free PPU)
 *   key: ask_ppu_credit_cents:<sub>   value: JSON { cents: number }, prepaid cents after $0.50 top-ups
 *
 * Retired AI counters, still READ for the transition, never written:
 *   key: ask_usage:<sub>:<yyyymm>      carrier_ask Pro monthly
 *   key: ask_daily:<sub>:<yyyymmdd>    carrier_ask free daily
 * Both held real production data. They are summed into the shared meter by
 * legacyAiBudgetKeys() in packages/carrier-ai/src/budget.ts, which also records
 * why the sum is a sum and when the branch can be deleted.
 *
 * Analytics Engine (carrier_mcp_audit dataset) is the source of truth for usage
 * rollups, KV counter is a fast hot path for quota checks.
 *
 * BILLING_PRIMARY feature flag (Doppler carrier/prd):
 *   "clerk"  → Clerk Billing is authoritative; KV is a cache/fallback.
 *              Portal redirects to Clerk-hosted billing page.
 *              Stripe webhook still runs (dual-write period; kept for 1 billing cycle).
 *   "stripe" → (default) Direct Stripe path; Clerk plan claim is a session-layer overlay.
 *
 * IMPORTANT: This module NEVER charges a card silently. It only:
 *   1. Gates tool calls based on tier + call count.
 *   2. Records usage records to Stripe Metered Billing (Pro overages).
 *   3. Creates Stripe PaymentIntents for Free-tier pay-per-use carrier_ask calls.
 *   4. Redirects users to the appropriate Billing Portal (Clerk or Stripe).
 */

import {
  AI_TIER_POLICY,
  readAiUsage,
  recordAiCalls,
  type AiTier,
} from "@carrier/ai";

import type { Env } from "./types.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Regular tool call limits (OCS API + intelligence composites). Mirrors apps/api/src/quota.ts. */
export const TIER_CALL_LIMITS: Record<Tier, number> = {
  free: 1_000,
  pro: Infinity,
  enterprise: Infinity,
  superadmin: Infinity,
};

/**
 * carrier_ask monthly quota (Pro tier).
 * Free tier uses CARRIER_ASK_FREE_DAILY_LIMIT instead.
 *
 * Re-exported from AI_TIER_POLICY rather than declared, because carrier_ask no
 * longer has a quota of its own: it spends the same allowance as the
 * intelligence composites, the REST API and the console. Two independent
 * constants for one allowance is how the two meters drifted apart in the first
 * place, and a drifted limit is invisible until an invoice is wrong.
 */
export const CARRIER_ASK_PRO_MONTHLY_LIMIT = AI_TIER_POLICY.pro.limit;

/**
 * Free tier: carrier_ask calls per day before CC is required.
 * Daily, and from the same shared policy. See above.
 */
export const CARRIER_ASK_FREE_DAILY_LIMIT = AI_TIER_POLICY.free.limit;

/**
 * Free tier carrier_ask pay-per-use price (USD cents per call).
 * Pro overage unit price is configured on the Stripe product.
 */
export const CARRIER_ASK_PPU_PRICE_CENTS = 2; // $0.02/call

/** Stripe minimum amount for a USD card charge (PaymentIntent). */
export const STRIPE_USD_MINIMUM_CHARGE_CENTS = 50;

export const UPGRADE_URL = "https://mcp.carrier.llc/upgrade";
export const ADD_PAYMENT_URL = "https://mcp.carrier.llc/billing/add-payment";

export type Tier = "free" | "pro" | "enterprise" | "superadmin";
export type ScopeToken = "read" | "write" | "admin";

/**
 * Commercial scopes. `admin` is deliberately absent from EVERY tier.
 *
 * It used to sit on `enterprise`, which meant a platform superadmin had to be a
 * paying Enterprise subscriber of Carrier's own product, and silently lost
 * operator rights whenever a subscription lapsed or a billing webhook glitched.
 * What `admin` gates is operator surface (balance mutation, template modify, SIM
 * status, package delete, fraud review), not a premium feature.
 *
 * Operator privilege now comes from membership of Carrier's own Clerk org, see
 * {@link isPlatformOperator}. Tier still grades read/write, which is
 * legitimately commercial.
 */
export const TIER_SCOPES: Record<Tier, ScopeToken[]> = {
  free: ["read"],
  pro: ["read", "write"],
  enterprise: ["read", "write"],
  // `superadmin` controls the master OCS credential, not authorization. Admin
  // still comes only from active membership of the configured platform org.
  superadmin: ["read", "write"],
};

/**
 * Clerk org id of Carrier's own organization, or null when unset/blank.
 *
 * Not a secret: an org id appears in any session token, so it lives in wrangler
 * vars rather than the secret store.
 */
export function platformOrgId(env: Env): string | null {
  const raw = (env as Env & { CARRIER_PLATFORM_ORG_ID?: string })
    .CARRIER_PLATFORM_ORG_ID;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * True only when the caller's ACTIVE organization is Carrier's own.
 *
 * Judged on the active org, not "any membership the user holds": the active org
 * is the session's real scope, and resolving from any membership would let a
 * session scoped to a tenant carry operator privilege.
 *
 * FAILS CLOSED, unset platform org means nobody is an operator.
 */
export function isPlatformOperator(
  env: Env,
  orgId: string | null | undefined,
): boolean {
  const platform = platformOrgId(env);
  if (!platform) return false;
  return Boolean(orgId) && orgId === platform;
}

// ---------------------------------------------------------------------------
// Phase 15, BILLING_PRIMARY feature flag
// ---------------------------------------------------------------------------

export type BillingPrimary = "clerk" | "stripe";

export function getBillingPrimary(env: Env): BillingPrimary {
  return env.BILLING_PRIMARY === "clerk" ? "clerk" : "stripe";
}

// ---------------------------------------------------------------------------
// getUserTier
// ---------------------------------------------------------------------------

export async function getUserTier(env: Env, sub: string): Promise<Tier> {
  // stdio: no Workers KV — treat as free tier, callers gate.
  if (!env.CARRIER_USERS) return "free";
  const raw = await env.CARRIER_USERS.get(`user:${sub}`, "json").catch(
    () => null,
  );
  if (
    raw &&
    typeof raw === "object" &&
    "tier" in raw &&
    (raw as { tier: string }).tier in TIER_CALL_LIMITS
  ) {
    return (raw as { tier: Tier }).tier;
  }
  return "free";
}

export function resolveTierFromClerk(
  clerkPlan: Tier | undefined,
  fallbackTier: Tier,
): Tier {
  return clerkPlan ?? fallbackTier;
}

// ---------------------------------------------------------------------------
// Portal URL resolution
// ---------------------------------------------------------------------------

export function buildClerkBillingPortalUrl(env: Env, returnUrl: string): string {
  const base =
    env.CLERK_BILLING_PORTAL_URL ?? "https://accounts.carrier.llc/user/billing";
  const url = new URL(base);
  url.searchParams.set("redirect_url", returnUrl);
  return url.toString();
}

export async function resolveBillingPortalUrl(
  env: Env,
  sub: string,
  returnUrl: string,
): Promise<string | null> {
  const primary = getBillingPrimary(env);

  if (primary === "clerk") {
    return buildClerkBillingPortalUrl(env, returnUrl);
  }

  const stripePortalUrl = await createBillingPortalSession(env, sub, returnUrl);
  if (stripePortalUrl) return stripePortalUrl;

  return buildClerkBillingPortalUrl(env, returnUrl);
}

// ---------------------------------------------------------------------------
// getScopeForTier
// ---------------------------------------------------------------------------

export function getScopeForTier(tier: Tier): ScopeToken[] {
  return TIER_SCOPES[tier];
}

/**
 * Final scope set: commercial scopes from tier, plus `admin` if and only if the
 * caller is a platform operator. Use this at every grant site; plain
 * {@link getScopeForTier} can never return admin.
 */
export function resolveScopes(
  env: Env,
  tier: Tier,
  orgId: string | null | undefined,
): ScopeToken[] {
  const scopes = [...TIER_SCOPES[tier]];
  if (isPlatformOperator(env, orgId) && !scopes.includes("admin")) {
    scopes.push("admin");
  }
  return scopes;
}

/**
 * Add `admin` to an ALREADY-STORED scope list when the org is the platform org.
 *
 * Stored `scopes_grant` is written by several heal paths (clerk-billing-webhook,
 * internal-tier-heal, oauth healKvFromClerkPlan) from the TIER alone, and no
 * tier grants admin any more. Without re-deriving at read time, any heal would
 * silently strip an operator's admin from their record, invisibly, because the
 * record still looks well-formed.
 *
 * Operator status is not a billing fact, so it must never be cached behind one.
 * Deriving it on read makes the stored value advisory rather than authoritative.
 */
export function withOperatorAdmin(
  env: Env,
  stored: ScopeToken[] | undefined,
  orgId: string | null | undefined,
): ScopeToken[] {
  const scopes = [...(stored ?? ["read"])];
  if (isPlatformOperator(env, orgId) && !scopes.includes("admin")) {
    scopes.push("admin");
  }
  return scopes;
}

// ---------------------------------------------------------------------------
// checkCallQuota, regular OCS/intelligence tools (not carrier_ask)
// ---------------------------------------------------------------------------

export interface QuotaResult {
  allowed: boolean;
  remaining: number;
  resetAt: string; // ISO 8601 first day of next month
  tier: Tier;
}

/**
 * Regular tool quota check (OCS API + intelligence composites).
 * Pro and Enterprise are always allowed. Free is capped at TIER_CALL_LIMITS.free.
 */
export async function checkCallQuota(
  env: Env,
  sub: string,
  tier: Tier,
): Promise<QuotaResult> {
  const limit = TIER_CALL_LIMITS[tier];
  const resetAt = firstDayNextMonth();

  if (tier === "pro" || tier === "enterprise" || tier === "superadmin") {
    return { allowed: true, remaining: Infinity, resetAt, tier };
  }

  // stdio: no Workers KV — nothing to meter against locally.
  if (!env.CARRIER_USERS) {
    return { allowed: true, remaining: Infinity, resetAt, tier };
  }

  const month = currentMonth();
  const usageKey = `usage:${sub}:${month}`;
  const raw = await env.CARRIER_USERS.get(usageKey, "json").catch(() => null);
  const calls: number =
    raw && typeof raw === "object" && "calls" in raw
      ? Number((raw as { calls: number }).calls)
      : 0;

  const remaining = Math.max(0, limit - calls);
  return {
    allowed: calls < limit,
    remaining,
    resetAt,
    tier,
  };
}

// ---------------------------------------------------------------------------
// carrier_ask quota, separate from regular tool quota
// ---------------------------------------------------------------------------

export type CarrierAskQuotaStatus =
  | { allowed: true; remaining: number; resetAt: string; tier: Tier }
  | { allowed: false; reason: "daily_limit_reached"; dailyUsed: number; dailyLimit: number; hasPaymentMethod: boolean; addPaymentUrl: string }
  | { allowed: false; reason: "monthly_limit_reached"; monthlyUsed: number; monthlyLimit: number; overageEnabled: boolean; upgradeUrl: string }
  | { allowed: false; reason: "enterprise_unlimited" }; // never fires, but satisfies TS

/**
 * Check whether a carrier_ask call is allowed for this user.
 *
 * Free tier:
 *   - Up to CARRIER_ASK_FREE_DAILY_LIMIT calls/day free.
 *   - Beyond that: allowed only if the user has a payment method on file.
 *     The caller is responsible for charging (chargeCarrierAskPpu).
 *
 * Pro tier:
 *   - Up to CARRIER_ASK_PRO_MONTHLY_LIMIT calls/mo included.
 *   - Beyond that: allowed (overage charged via Stripe metered billing).
 *
 * Enterprise: always allowed.
 *
 * The counts come from the shared AI meter (@carrier/ai), not from a
 * carrier_ask-only key. Before that, a Pro tenant could spend 1,000 calls here
 * and another 1,000 through the composites, the REST API and the console,
 * because each surface metered the same allowance on its own key. Who is
 * allowed and who is refused is unchanged; only where the number is read from.
 */
export async function checkCarrierAskQuota(
  env: Env,
  sub: string,
  tier: Tier,
): Promise<CarrierAskQuotaStatus> {
  if (tier === "enterprise" || tier === "superadmin") {
    return { allowed: true, remaining: Infinity, resetAt: firstDayNextMonth(), tier };
  }

  // stdio: no Workers KV — the shared AI meter is unreachable, so there is no
  // number to refuse on. The hosted Worker still meters the same tenant.
  if (!env.CARRIER_USERS) {
    return { allowed: true, remaining: Infinity, resetAt: firstDayNextMonth(), tier };
  }

  if (tier === "pro") {
    const usage = await readAiUsage(env.CARRIER_USERS, sub, "pro");
    // Pro always allowed, overages are charged automatically
    return {
      allowed: true,
      remaining: usage.remaining,
      resetAt: usage.resetsAt,
      tier,
    };
  }

  // Free tier, daily limit gate
  const usage = await readAiUsage(env.CARRIER_USERS, sub, "free");
  const dailyUsed = usage.used;

  if (dailyUsed < CARRIER_ASK_FREE_DAILY_LIMIT) {
    return {
      allowed: true,
      remaining: usage.remaining,
      resetAt: usage.resetsAt,
      tier,
    };
  }

  // Daily limit reached, check for payment method
  const pmId = await env.CARRIER_USERS.get(`stripe_pm_id:${sub}`).catch(() => null);
  return {
    allowed: false,
    reason: "daily_limit_reached",
    dailyUsed,
    dailyLimit: CARRIER_ASK_FREE_DAILY_LIMIT,
    hasPaymentMethod: !!pmId,
    addPaymentUrl: ADD_PAYMENT_URL,
  };
}

// ---------------------------------------------------------------------------
// recordCarrierAskUsage, fire-and-forget after a successful carrier_ask call
// ---------------------------------------------------------------------------

/**
 * Charge one carrier_ask call against the shared AI allowance.
 *
 * `recordAiCalls`, not `reserveAiCalls`: Pro is always allowed here and billed
 * for the overage, so the counter has to keep climbing past 1,000. A
 * reservation refuses at the limit and writes nothing, which would freeze the
 * count at exactly the point overage billing starts and leave every further
 * call unbilled.
 *
 * Enterprise is not metered here, as before. It is contract-billed, its
 * carrier_ask calls have never been counted, and starting to count them would
 * make its REST-side allowance run out for reasons no one changed.
 */
export function recordCarrierAskUsage(env: Env, sub: string, tier: Tier): void {
  // stdio: no Workers KV — see the header note on the crash this prevents.
  if (!env.CARRIER_USERS) return;

  (async () => {
    if (tier === "enterprise" || tier === "superadmin") return;

    const usage = await recordAiCalls(env.CARRIER_USERS, sub, tier as AiTier);

    // Push Stripe overage record when over the included limit. `usage.used`
    // includes what this tenant spent on the other AI surfaces, which is the
    // point: one allowance, so the overage starts once the allowance is gone,
    // wherever it went.
    if (
      tier === "pro" &&
      usage.used > CARRIER_ASK_PRO_MONTHLY_LIMIT &&
      getBillingPrimary(env) === "stripe"
    ) {
      await pushCarrierAskOverageRecord(env, sub, 1).catch(() => {});
    }
  })();
}

// ---------------------------------------------------------------------------
// chargeCarrierAskPpu, charge a Free-tier user for a pay-per-use call
// ---------------------------------------------------------------------------

export interface PpuChargeResult {
  charged: boolean;
  amountCents: number;
  paymentIntentId?: string;
  error?: string;
}

/**
 * Charge a Free-tier user $0.02 for a carrier_ask call via their saved payment method.
 * Stripe enforces a USD minimum per PaymentIntent; surplus is stored as prepaid credit in KV.
 * Returns { charged: false } if Stripe is not configured or no payment method exists.
 * Non-fatal, caller decides whether to block or allow on charge failure.
 */
export async function chargeCarrierAskPpu(
  env: Env,
  sub: string,
): Promise<PpuChargeResult> {
  const stripeKey = (env as Env & { STRIPE_SECRET_KEY?: string }).STRIPE_SECRET_KEY;
  if (!stripeKey) return { charged: false, amountCents: CARRIER_ASK_PPU_PRICE_CENTS, error: "stripe_not_configured" };
  if (!env.CARRIER_USERS) return { charged: false, amountCents: CARRIER_ASK_PPU_PRICE_CENTS, error: "no_payment_method" };

  const customerId = await env.CARRIER_USERS.get(`stripe_customer_id:${sub}`).catch(() => null);
  const pmId = await env.CARRIER_USERS.get(`stripe_pm_id:${sub}`).catch(() => null);

  if (!customerId || !pmId) {
    return { charged: false, amountCents: CARRIER_ASK_PPU_PRICE_CENTS, error: "no_payment_method" };
  }

  const creditKey = `ask_ppu_credit_cents:${sub}`;
  const rawCredit = await env.CARRIER_USERS.get(creditKey, "json").catch(() => null);
  let creditCents =
    rawCredit && typeof rawCredit === "object" && "cents" in rawCredit
      ? Number((rawCredit as { cents: number }).cents)
      : 0;
  if (!Number.isFinite(creditCents) || creditCents < 0) creditCents = 0;

  if (creditCents >= CARRIER_ASK_PPU_PRICE_CENTS) {
    const nextCredit = creditCents - CARRIER_ASK_PPU_PRICE_CENTS;
    await env.CARRIER_USERS.put(
      creditKey,
      JSON.stringify({ cents: nextCredit, updated_at: new Date().toISOString() }),
    );
    return { charged: true, amountCents: CARRIER_ASK_PPU_PRICE_CENTS };
  }

  const stripeChargeCents = STRIPE_USD_MINIMUM_CHARGE_CENTS;

  const body = new URLSearchParams({
    amount: String(stripeChargeCents),
    currency: "usd",
    customer: customerId,
    payment_method: pmId,
    confirm: "true",
    "automatic_payment_methods[enabled]": "true",
    "automatic_payment_methods[allow_redirects]": "never",
    description: "carrier_ask pay-per-use call",
    "metadata[sub]": sub,
    "metadata[type]": "carrier_ask_ppu",
  });

  const resp = await fetch("https://api.stripe.com/v1/payment_intents", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${stripeKey}`,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  });

  if (!resp.ok) {
    const err = await resp.text().catch(() => "unknown");
    return { charged: false, amountCents: CARRIER_ASK_PPU_PRICE_CENTS, error: `stripe_error: ${err}` };
  }

  const pi = (await resp.json()) as { id: string; status: string };
  if (pi.status !== "succeeded") {
    return { charged: false, amountCents: CARRIER_ASK_PPU_PRICE_CENTS, error: `payment_status: ${pi.status}` };
  }

  const nextCredit = creditCents + stripeChargeCents - CARRIER_ASK_PPU_PRICE_CENTS;
  await env.CARRIER_USERS.put(
    creditKey,
    JSON.stringify({ cents: nextCredit, updated_at: new Date().toISOString() }),
  );

  return { charged: true, amountCents: CARRIER_ASK_PPU_PRICE_CENTS, paymentIntentId: pi.id };
}

// ---------------------------------------------------------------------------
// recordUsage, regular OCS/intelligence tools (unchanged)
// ---------------------------------------------------------------------------

export function recordUsage(env: Env, sub: string, tier: Tier): void {
  // stdio: no Workers KV — see the header note on the crash this prevents.
  if (!env.CARRIER_USERS) return;

  (async () => {
    if (tier === "pro" || tier === "enterprise" || tier === "superadmin") return; // unlimited — no counter needed

    const month = currentMonth();
    const usageKey = `usage:${sub}:${month}`;
    const raw = await env.CARRIER_USERS.get(usageKey, "json").catch(() => null);
    const prev: number =
      raw && typeof raw === "object" && "calls" in raw
        ? Number((raw as { calls: number }).calls)
        : 0;
    const next = prev + 1;

    await env.CARRIER_USERS.put(
      usageKey,
      JSON.stringify({ calls: next, updated_at: new Date().toISOString() }),
      { expirationTtl: 35 * 24 * 60 * 60 },
    );
  })();
}

// ---------------------------------------------------------------------------
// pushCarrierAskOverageRecord (internal, Pro tier Stripe metered overage)
// ---------------------------------------------------------------------------

async function pushCarrierAskOverageRecord(
  env: Env,
  sub: string,
  quantity: number,
): Promise<void> {
  const stripeKey = (env as Env & { STRIPE_SECRET_KEY?: string }).STRIPE_SECRET_KEY;
  if (!stripeKey) return;
  if (!env.CARRIER_USERS) return;

  const subItemId = await env.CARRIER_USERS.get(`stripe_ask_sub_item_id:${sub}`);
  if (!subItemId) return;

  const body = new URLSearchParams({
    quantity: String(quantity),
    timestamp: String(Math.floor(Date.now() / 1000)),
    action: "increment",
  });

  await fetch(
    `https://api.stripe.com/v1/subscription_items/${subItemId}/usage_records`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${stripeKey}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
  );
}

// ---------------------------------------------------------------------------
// pushStripeUsageRecord (kept for legacy regular-tool metering, Stripe-primary only)
// ---------------------------------------------------------------------------

async function pushStripeUsageRecord(
  env: Env,
  sub: string,
  quantity: number,
): Promise<void> {
  const stripeKey = (env as Env & { STRIPE_SECRET_KEY?: string }).STRIPE_SECRET_KEY;
  if (!stripeKey) return;
  if (!env.CARRIER_USERS) return;

  const subItemId = await env.CARRIER_USERS.get(`stripe_sub_item_id:${sub}`);
  if (!subItemId) return;

  const body = new URLSearchParams({
    quantity: String(quantity),
    timestamp: String(Math.floor(Date.now() / 1000)),
    action: "increment",
  });

  await fetch(
    `https://api.stripe.com/v1/subscription_items/${subItemId}/usage_records`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${stripeKey}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
  );
}

// ---------------------------------------------------------------------------
// createBillingPortalSession (Stripe, kept for dual-write period)
// ---------------------------------------------------------------------------

export async function createBillingPortalSession(
  env: Env,
  sub: string,
  returnUrl: string,
): Promise<string | null> {
  const stripeKey = (env as Env & { STRIPE_SECRET_KEY?: string }).STRIPE_SECRET_KEY;
  if (!stripeKey) return null;
  if (!env.CARRIER_USERS) return null;

  const customerId = await env.CARRIER_USERS.get(`stripe_customer_id:${sub}`);
  if (!customerId) return null;

  const body = new URLSearchParams({
    customer: customerId,
    return_url: returnUrl,
  });

  const resp = await fetch(
    "https://api.stripe.com/v1/billing_portal/sessions",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${stripeKey}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
  );

  if (!resp.ok) return null;
  const data = (await resp.json()) as { url?: string };
  return data.url ?? null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function currentMonth(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

function firstDayNextMonth(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  if (m === 12) {
    return new Date(Date.UTC(y + 1, 0, 1)).toISOString();
  }
  return new Date(Date.UTC(y, m, 1)).toISOString();
}

// Keep pushStripeUsageRecord accessible for tests
export { pushStripeUsageRecord as _pushStripeUsageRecord };
